/**
 * TURN CC · 25 — Event details side panel.
 *
 * Complete refresh of the calendar/kanban click-through side panel.
 * Priorities: strong hierarchy, WCAG-AA contrast, sticky header, coherent
 * type/status semantics, and a dedicated visual for corporate events.
 *
 * Structure:
 *   ┌────────────────────────────────────────────┐
 *   │ [type pill]                    [status]    │  ← sticky header
 *   │ Title                                       │
 *   │ Localised long date · HH:MM SGT             │
 *   ├────────────────────────────────────────────┤
 *   │ TIMING          Scheduled / Executed        │
 *   │ CONTENT         Articles / Sources / etc.   │
 *   │ [Primary CTA]                                │
 *   │ <details> Advanced (brief_id, timestamps)   │
 *   └────────────────────────────────────────────┘
 *
 * Corporate events replace the header with a gradient hero band and swap
 * the timing/content sections for location/duration/description blocks.
 */
import { Link } from "react-router-dom";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Clock, Play, CheckCircle2, AlertTriangle, Send,
  FileText, Mail, MapPin, ExternalLink, ChevronDown, Copy,
} from "lucide-react";
import {
  TYPE_META, formatSGTHour, formatSGTLongDate, formatSGTTime,
  formatAllDayLongDate,
} from "./calendarMeta";

// ---------- Semantic status meta (rich, panel-only) ----------
const PANEL_STATUS = {
  scheduled: {
    label: "Scheduled",
    Icon: Clock,
    classes:
      "bg-slate-100 text-slate-700 border-slate-200 " +
      "dark:bg-white/5 dark:text-slate-300 dark:border-white/10",
  },
  running: {
    label: "Running",
    Icon: Play,
    classes:
      "bg-blue-50 text-blue-700 border-blue-200 " +
      "dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30",
    pulse: true,
  },
  completed: {
    label: "Ready for review",
    Icon: Send,
    classes:
      "bg-amber-50 text-amber-800 border-amber-200 " +
      "dark:bg-amber-500/15 dark:text-amber-200 dark:border-amber-500/30",
  },
  ready_for_review: {
    label: "Ready for review",
    Icon: Send,
    classes:
      "bg-amber-50 text-amber-800 border-amber-200 " +
      "dark:bg-amber-500/15 dark:text-amber-200 dark:border-amber-500/30",
  },
  delivered: {
    label: "Delivered",
    Icon: CheckCircle2,
    classes:
      "bg-emerald-50 text-emerald-700 border-emerald-200 " +
      "dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30",
  },
  failed: {
    label: "Failed",
    Icon: AlertTriangle,
    classes:
      "bg-red-50 text-red-700 border-red-200 " +
      "dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30",
  },
};

function StatusPill({ status }) {
  const meta = PANEL_STATUS[status] || PANEL_STATUS.scheduled;
  const Icon = meta.Icon;
  return (
    <Badge
      variant="outline"
      data-testid="event-details-status"
      className={
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold border " +
        meta.classes
      }
    >
      <Icon className="h-3 w-3" />
      {meta.label}
      {meta.pulse && (
        <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse" />
      )}
    </Badge>
  );
}

function TypePill({ type }) {
  const meta = TYPE_META[type] || {};
  const Icon = meta.Icon;
  return (
    <span
      data-testid="event-details-type-pill"
      className={
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 " +
        "text-[11px] font-medium capitalize " +
        (meta.chipActive || "bg-slate-500 text-white")
      }
    >
      {Icon && <Icon className="h-3 w-3" />}
      {meta.label || type}
    </span>
  );
}

function SectionTitle({ children }) {
  return (
    <h4 className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-2">
      {children}
    </h4>
  );
}

function Field({ icon: Icon, label, value, valueClass = "" }) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="inline-flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {label}
      </div>
      <div className={"text-sm font-medium text-slate-900 dark:text-slate-100 " + valueClass}>
        {value}
      </div>
    </div>
  );
}

function CopyableId({ value }) {
  const copy = () => {
    try { navigator.clipboard?.writeText(value); } catch { /* noop */ }
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid="event-details-copy-id"
      className="inline-flex items-center gap-1 font-mono text-[11px] text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
      title="Copy to clipboard"
    >
      <span className="truncate max-w-[220px]">{value}</span>
      <Copy className="h-3 w-3 shrink-0 opacity-60" />
    </button>
  );
}

// ---------- Corporate event body ----------
function CorporateBody({ event }) {
  const days = (() => {
    if (!event.starts_at || !event.ends_at) return 1;
    const s = new Date(event.starts_at);
    const e = new Date(event.ends_at);
    return Math.max(1, Math.floor((e - s) / 86400000) + 1);
  })();
  return (
    <div className="mt-6 space-y-6">
      {(event.location || event.venue) && (
        <section>
          <SectionTitle>Location</SectionTitle>
          <div className="flex items-start gap-2">
            <MapPin className="h-4 w-4 text-slate-400 dark:text-slate-500 mt-0.5 shrink-0" />
            <div className="text-sm text-slate-800 dark:text-slate-200 leading-snug">
              {event.venue && (
                <div className="font-medium">{event.venue}</div>
              )}
              {event.location && (
                <div className="text-slate-500 dark:text-slate-400">
                  {event.location}
                </div>
              )}
            </div>
          </div>
        </section>
      )}
      <section>
        <SectionTitle>Duration</SectionTitle>
        <div className="grid grid-cols-2 gap-4">
          <Field icon={Clock} label="Days" value={`${days} day${days > 1 ? "s" : ""}`} />
          <Field
            icon={Clock}
            label="Range"
            value={`${formatAllDayLongDate(event.starts_at)} → ${formatAllDayLongDate(event.ends_at)}`}
            valueClass="text-[13px] font-normal"
          />
        </div>
      </section>
      {event.description && (
        <section>
          <SectionTitle>Description</SectionTitle>
          <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            {event.description}
          </p>
        </section>
      )}
      {event.url && (
        <div>
          <Button
            asChild
            data-testid="event-details-cta"
            className="w-full bg-slate-900 hover:bg-slate-800 text-white dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
          >
            <a href={event.url} target="_blank" rel="noreferrer">
              View on beyondactiv.com
              <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
            </a>
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------- Brief body ----------
function BriefBody({ event }) {
  const isScheduled = event.status === "scheduled";
  const isFailed = event.status === "failed";
  const hasBrief = Boolean(event.brief_id);
  const articles = event.articles_count || 0;
  const executedIso = event.email_sent_at
    || (hasBrief && event.status !== "scheduled" && event.status !== "running"
        ? event.ends_at : null);

  return (
    <div className="mt-6 space-y-6">
      {/* TIMING */}
      <section>
        <SectionTitle>Timing</SectionTitle>
        <div className="grid grid-cols-2 gap-4">
          <Field
            icon={Clock}
            label="Scheduled"
            value={`${formatSGTHour(event.starts_at)} SGT`}
          />
          <Field
            icon={CheckCircle2}
            label="Executed"
            value={
              executedIso ? (
                <span className="text-emerald-700 dark:text-emerald-400">
                  {formatSGTHour(executedIso)} SGT
                </span>
              ) : (
                <span className="text-slate-400 dark:text-slate-500">—</span>
              )
            }
          />
        </div>
      </section>

      {/* CONTENT */}
      {(articles > 0 || event.email_sent_at) && (
        <section>
          <SectionTitle>Content</SectionTitle>
          <div className="grid grid-cols-2 gap-4">
            {articles > 0 && (
              <Field
                icon={FileText}
                label={event.type === "weekly_quotes" ? "Quotes" : "Articles"}
                value={articles}
              />
            )}
            {event.email_sent_at && (
              <Field
                icon={Mail}
                label="Delivered"
                value={
                  <span className="text-emerald-700 dark:text-emerald-400">
                    {formatSGTTime(event.email_sent_at)}
                  </span>
                }
              />
            )}
          </div>
        </section>
      )}

      {/* FAILURE REASON */}
      {isFailed && event.failure_reason && (
        <section>
          <SectionTitle>Error</SectionTitle>
          <div className="rounded-md bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 p-3">
            <p className="text-[13px] text-red-800 dark:text-red-300 leading-relaxed">
              {event.failure_reason}
            </p>
          </div>
        </section>
      )}

      {/* ACTIONS */}
      <div className="space-y-2">
        {hasBrief ? (
          <Button
            asChild
            data-testid="event-details-cta"
            className="w-full bg-slate-900 hover:bg-slate-800 text-white dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
          >
            <Link to={event.url}>
              View brief
              <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
            </Link>
          </Button>
        ) : (
          <Button
            disabled
            data-testid="event-details-cta-disabled"
            className="w-full bg-slate-100 text-slate-500 dark:bg-white/5 dark:text-slate-400 cursor-not-allowed"
          >
            Not yet generated — will run at scheduled time
          </Button>
        )}
        {isFailed && (
          <Button
            variant="outline"
            disabled
            data-testid="event-details-retry-disabled"
            title="Contact admin to retry"
            className="w-full border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400 cursor-not-allowed"
          >
            <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />
            Retry (contact admin)
          </Button>
        )}
      </div>

      {/* ADVANCED */}
      <details className="group border-t border-slate-200 dark:border-white/10 pt-4" data-testid="event-details-advanced">
        <summary className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 cursor-pointer hover:text-slate-600 dark:hover:text-slate-300 list-none">
          <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
          Advanced
        </summary>
        <dl className="mt-3 space-y-2 text-[12px]">
          {event.brief_id && (
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-slate-500 dark:text-slate-400">Brief ID</dt>
              <dd><CopyableId value={event.brief_id} /></dd>
            </div>
          )}
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-slate-500 dark:text-slate-400">Event ID</dt>
            <dd><CopyableId value={event.id} /></dd>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-slate-500 dark:text-slate-400">Source</dt>
            <dd className="font-medium text-slate-700 dark:text-slate-300">
              {event.metadata?.virtual ? "Cron slot" : "Manual / auto"}
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-slate-500 dark:text-slate-400">Slot date</dt>
            <dd className="font-medium text-slate-700 dark:text-slate-300">
              {event.slot_date || "—"}
            </dd>
          </div>
        </dl>
      </details>
    </div>
  );
}

// ---------- Public component ----------
export default function EventDetailsPanel({ event, open, onOpenChange }) {
  if (!event) return null;
  const isCorporate = event.type === "corporate_event";
  const typeMeta = TYPE_META[event.type] || {};

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="event-details-panel"
        className={
          "w-full sm:max-w-md p-0 overflow-y-auto " +
          "bg-white dark:bg-slate-950 " +
          "border-l border-slate-200 dark:border-white/10 " +
          "text-slate-900 dark:text-slate-100"
        }
      >
        {isCorporate ? (
          <>
            {/* Gradient hero header */}
            <div
              data-testid="event-details-corporate-hero"
              className="relative h-24 w-full bg-gradient-to-br from-red-500 to-rose-600 text-white flex flex-col justify-end px-6 py-4"
            >
              <div className="text-[10px] font-semibold uppercase tracking-widest opacity-90">
                {formatAllDayLongDate(event.starts_at)}
                {" → "}
                {formatAllDayLongDate(event.ends_at)}
              </div>
              <SheetTitle className="text-lg font-semibold text-white mt-0.5 pr-10">
                {event.title}
              </SheetTitle>
            </div>
            <div className="px-6 pb-6">
              <CorporateBody event={event} />
            </div>
          </>
        ) : (
          <>
            {/* Sticky header */}
            <div className="sticky top-0 z-10 bg-white dark:bg-slate-950 px-6 pt-6 pb-4 border-b border-slate-200 dark:border-white/10">
              <div className="flex items-center justify-between gap-2 mb-3">
                <TypePill type={event.type} />
                <StatusPill status={event.status} />
              </div>
              <SheetTitle className="text-xl font-semibold text-slate-900 dark:text-white leading-tight">
                {event.title}
              </SheetTitle>
              <SheetDescription className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                {formatSGTLongDate(event.starts_at)}
                <span className="mx-1.5 opacity-40">·</span>
                <span className="tabular-nums">
                  {formatSGTHour(event.starts_at)} SGT
                </span>
              </SheetDescription>
            </div>
            <div className="px-6 pb-8">
              <BriefBody event={event} />
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
